// Résolution pure du libellé d'une catégorie de grille, partagée par l'éditeur,
// le détail d'évaluation et le PDF individuel (GitLab #1062). Une grille a soit
// des catégories personnalisées (`customCategories`, clé = id), soit les quatre
// catégories standard, dont le libellé est fourni par l'appelant (`fallback`).

import { pickLocalized } from "../../../../utils/translations/localizedData";

export const hasCustomCategories = (customCategories) =>
  Array.isArray(customCategories) && customCategories.length > 0;

export const findCustomCategory = (categoryKey, customCategories) => {
  if (!hasCustomCategories(customCategories) || categoryKey === undefined || categoryKey === null) return null;
  return customCategories.find((category) => String(category.id) === String(categoryKey)) || null;
};

// « nl-NL » → « nl » ; tout ce qui n'est pas un code de langue (clé i18n non traduite…) → « fr ».
export const baseLanguage = (language) => {
  const base = String(language || "").split(/[-_]/)[0].toLowerCase();
  return /^[a-z]{2,3}$/.test(base) ? base : "fr";
};

/**
 * Texte multilingue d'une grille ou d'une donnée commune : la langue demandée,
 * puis l'anglais, puis le français, puis toute autre langue renseignée
 * (décision du 03/11/2026, `pickLocalized`) ; rien d'exploitable → null, à
 * l'appelant de signaler le libellé manquant plutôt que d'afficher un identifiant.
 */
export const localizedText = (value, language = "fr") => pickLocalized(value, baseLanguage(language)) || null;

export const localizedCategoryName = (category, language = "fr", missing = null) =>
  localizedText(category?.name, language) || missing || String(category?.id ?? "");

/**
 * @param {object} params
 * @param {string|number} params.categoryKey id de catégorie personnalisée, ou clé standard (1-4, "sante"…)
 * @param {Array|null} params.customCategories catégories de la grille
 * @param {string} params.language langue d'affichage
 * @param {(key) => string|null|undefined} params.fallback libellé d'une clé standard (i18n, table PDF…)
 * @param {string|null} params.missing texte affiché quand la catégorie n'a de nom dans aucune langue
 * @returns {string} jamais une clé i18n brute : au pire, `missing` ou la clé elle-même
 */
export const resolveCategoryLabel = ({ categoryKey, customCategories = null, language = "fr", fallback = null, missing = null }) => {
  const custom = findCustomCategory(categoryKey, customCategories);
  if (custom) return localizedCategoryName(custom, language, missing);
  const label = typeof fallback === "function" ? fallback(categoryKey) : null;
  return label || String(categoryKey ?? "");
};

/** Libellé i18n d'une catégorie standard de l'éditeur (clés `grid.category.1` à `.4`), sinon null. */
export const standardGridCategoryLabel = (t) => (categoryKey) =>
  ["1", "2", "3", "4"].includes(String(categoryKey)) ? t(`grid.category.${categoryKey}`) : null;

/** Identifiant de catégorie d'une question chargée pour l'éditeur. */
export const questionCategoryKey = (question) => {
  const raw = question?.questionCategory;
  const plain = raw !== null && typeof raw === "object" ? null : raw;
  return question?.questionCategoryId ?? raw?.value ?? plain ?? null;
};
